'use client'

import { useEffect, useRef } from 'react'
import posthog from 'posthog-js'
import { isNativePlatform } from '@/lib/utils/platform'
import {
  buildAppVersionProbeUrl,
  buildCacheBustedReloadUrl,
  canAttemptWebDeployReload,
  parseReloadAttemptCount,
  parseWebBuildIdFromAppVersionPayload,
  readClientWebBuildId,
  shouldReloadForWebBuild,
  WEB_DEPLOY_RELOAD_ATTEMPTS_KEY,
  WEB_DEPLOY_RELOAD_KEY,
} from '@/lib/native/web-deploy-sync'
import {
  safeGetSessionItem,
  safeRemoveSessionItem,
  safeSetSessionItem,
} from '@/lib/utils/safe-storage'

/**
 * Keeps Capacitor remote WebViews on the latest Vercel deploy without user
 * action. On cold launch and every foreground resume, compares the build id
 * baked into this bundle against `/api/app-version` (no-store). Mismatch →
 * cache-busted navigation (up to 3 tries per session) so WKWebView disk cache
 * is busted.
 *
 * Complements `instrumentation-client.ts` chunk/action auto-reload (mid-session
 * deploy safety net). Native intentionally skips the service worker registrar.
 */
export function WebDeploySync() {
  const checkingRef = useRef(false)

  useEffect(() => {
    if (!isNativePlatform()) return

    let cancelled = false
    let removeAppStateListener: (() => void) | undefined

    async function checkForNewWebDeploy(trigger: 'mount' | 'resume') {
      if (cancelled || checkingRef.current) return

      checkingRef.current = true
      try {
        // Unique URL per poll — WKWebView ignores `cache: 'no-store'` and the
        // response's `no-store` header, re-serving a stale (disk-cached) payload
        // with the OLD build id, which defeats the mismatch check entirely. The
        // nonce gives the cache no key to hit, forcing a real network response.
        // The probe must NOT carry a Vercel skew `?dpl=` — it has to reach the
        // LATEST deployment, not the client's pinned one, or the check is moot.
        const res = await fetch(buildAppVersionProbeUrl(String(Date.now())), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
        if (!res.ok) {
          // Surface probe failures so "never runs on device" and "always fails"
          // stay distinguishable in telemetry (the whole point of the heartbeat).
          posthog.capture('web_deploy_check_failed', { trigger, status: res.status })
          return
        }

        const data: unknown = await res.json()
        const serverBuildId = parseWebBuildIdFromAppVersionPayload(data)
        const clientBuildId = readClientWebBuildId()
        const isStale = shouldReloadForWebBuild(clientBuildId, serverBuildId)

        // Heartbeat on EVERY check (not just reloads) so native freshness is
        // observable: confirms the probe runs on device, whether build ids ever
        // diverge, and whether reloads fire when they should. Without this, a
        // self-heal that never runs and one that's never needed look identical
        // (both emit nothing).
        posthog.capture('web_deploy_check', {
          trigger,
          client_build_id: clientBuildId,
          server_build_id: serverBuildId,
          is_stale: isStale,
          vercel_id: res.headers.get('x-vercel-id'),
        })

        if (!isStale) {
          safeRemoveSessionItem(WEB_DEPLOY_RELOAD_KEY)
          safeRemoveSessionItem(WEB_DEPLOY_RELOAD_ATTEMPTS_KEY)
          return
        }

        const attempts = parseReloadAttemptCount(safeGetSessionItem(WEB_DEPLOY_RELOAD_ATTEMPTS_KEY))
        if (!canAttemptWebDeployReload(attempts)) {
          posthog.capture('web_deploy_reload_exhausted', {
            trigger,
            client_build_id: clientBuildId,
            server_build_id: serverBuildId,
            attempts,
          })
          return
        }

        const nextAttempt = attempts + 1
        safeSetSessionItem(WEB_DEPLOY_RELOAD_ATTEMPTS_KEY, String(nextAttempt))
        safeSetSessionItem(WEB_DEPLOY_RELOAD_KEY, trigger)

        posthog.capture('web_deploy_reload', {
          trigger,
          client_build_id: clientBuildId,
          server_build_id: serverBuildId,
          attempt: nextAttempt,
        })

        const url = buildCacheBustedReloadUrl(window.location.href, String(Date.now()))
        window.location.replace(url)
      } catch {
        // Non-critical — never break the app over a version poll failure. Still
        // surface it (network error / JSON parse) so the offline/failure rate is
        // visible and not mistaken for the probe never running on device.
        posthog.capture('web_deploy_check_failed', { trigger, status: 0 })
      } finally {
        checkingRef.current = false
      }
    }

    void checkForNewWebDeploy('mount')

    void import('@capacitor/app').then(({ App }) => {
      if (cancelled) return
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void checkForNewWebDeploy('resume')
      }).then((handle) => {
        if (cancelled) {
          void handle.remove()
          return
        }
        removeAppStateListener = () => {
          void handle.remove()
        }
      })
    })

    return () => {
      cancelled = true
      removeAppStateListener?.()
    }
  }, [])

  return null
}
